import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@progress/kendo-react-buttons';
import { trashIcon } from '@progress/kendo-svg-icons';
import { useFormBuilder } from '../context/FormBuilderContext';
import type { Field } from '../types';
import { encodeDragId } from '../utils/dnd';
import { fieldWidthPercent } from '../utils/fieldWidth';

export function FieldBlock({ field }: { field: Field }) {
  const { fieldTypeRegistry, selectedFieldId, selectField, removeField, isEditable } = useFormBuilder();
  const def = fieldTypeRegistry[field.fieldType];

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: encodeDragId({ kind: 'field', fieldId: field.id }),
    disabled: !isEditable,
  });

  const isSelected = selectedFieldId === field.id;

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        // flex-shrink (the "1") lets the row absorb the container's gap instead of
        // wrapping early; flex-basis is the configured fraction of the row this field
        // occupies — siblings whose widths sum to <= 100% sit side by side.
        flex: `0 1 ${fieldWidthPercent(field.layout?.width)}%`,
        minWidth: 0,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--kendo-spacing-2)',
        padding: 'var(--kendo-spacing-2) var(--kendo-spacing-3)',
        borderRadius: 'var(--kendo-border-radius-md)',
        border: `1px solid ${isSelected ? 'var(--kendo-color-primary)' : 'var(--kendo-color-border)'}`,
        background: isSelected ? 'var(--kendo-color-primary-subtle)' : 'var(--kendo-color-app-surface)',
        cursor: 'pointer',
      }}
      onClick={() => selectField(field.id)}
    >
      {isEditable && (
        <span {...attributes} {...listeners} style={{ cursor: 'grab', color: 'var(--kendo-color-subtle)', fontSize: 'var(--kendo-font-size-sm)' }} title="Drag to reorder">
          ⠿
        </span>
      )}
      <span style={{ fontSize: 'var(--kendo-font-size-md)' }}>{def?.icon ?? '❔'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 'var(--kendo-font-size-sm)',
            fontWeight: 'var(--kendo-font-weight-bold)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {field.label}
          {field.isRequired && <span style={{ color: 'var(--kendo-color-error)' }}> *</span>}
        </div>
        <div style={{ fontSize: 'var(--kendo-font-size-xs)', color: 'var(--kendo-color-subtle)' }}>
          {def?.label ?? field.fieldType} · {field.handle}
        </div>
      </div>
      {isEditable && (
        <Button
          fillMode="flat"
          svgIcon={trashIcon}
          onClick={(e) => {
            e.stopPropagation();
            removeField(field.id);
          }}
          title="Remove field"
        />
      )}
    </div>
  );
}
